import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CoverageResponse,
  DocumentType,
  DocumentTypeInput,
  ExpiringResponse,
  ProductDocumentsResponse,
  UploadDocumentResponse,
  UploadDocumentVars,
} from './types'

const BASE = '/api/product-documents'
const KEY = ['product-documents'] as const

export function useDocumentTypes(includeInactive = false) {
  return useQuery({
    queryKey: [...KEY, 'types', includeInactive],
    queryFn: () =>
      jsonFetch<DocumentType[]>(`${BASE}/types${includeInactive ? '?include_inactive=true' : ''}`),
    staleTime: 60_000,
  })
}

export function useProductDocuments(productId: number | null) {
  return useQuery({
    queryKey: [...KEY, 'product', productId],
    queryFn: () => jsonFetch<ProductDocumentsResponse>(`${BASE}/products/${productId}`),
    enabled: productId != null,
    staleTime: 10_000,
  })
}

export function useExpiringDocuments(days: number) {
  return useQuery({
    queryKey: [...KEY, 'expiring', days],
    queryFn: () => jsonFetch<ExpiringResponse>(`${BASE}/expiring?days=${days}`),
    staleTime: 60_000,
  })
}

export interface CoverageQuery {
  q?: string
  onlyMissing: boolean
  offset: number
  limit: number
}

export function useDocumentCoverage(query: CoverageQuery) {
  const params = new URLSearchParams()
  if (query.q) params.set('q', query.q)
  params.set('only_missing', String(query.onlyMissing))
  params.set('offset', String(query.offset))
  params.set('limit', String(query.limit))
  const qs = params.toString()
  return useQuery({
    queryKey: [...KEY, 'coverage', qs],
    queryFn: () => jsonFetch<CoverageResponse>(`${BASE}/coverage?${qs}`),
    staleTime: 60_000,
  })
}

export function useUploadProductDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: UploadDocumentVars) => {
      const form = new FormData()
      form.set('file', vars.file)
      form.set('doc_type_key', vars.docTypeKey)
      if (vars.versionLabel) form.set('version_label', vars.versionLabel)
      if (vars.issuedOn) form.set('issued_on', vars.issuedOn)
      if (vars.expiresOn) form.set('expires_on', vars.expiresOn)
      if (vars.notes) form.set('notes', vars.notes)
      return jsonFetch<UploadDocumentResponse>(`${BASE}/products/${vars.productId}/documents`, {
        method: 'POST',
        rawBody: form,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useArchiveProductDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<{ id: number; status: string }>(`${BASE}/documents/${id}/archive`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useCreateDocumentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: DocumentTypeInput & { key: string; label: string }) =>
      jsonFetch<DocumentType>(`${BASE}/types`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'types'] }),
  })
}

export function useUpdateDocumentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { key: string; values: DocumentTypeInput }) =>
      jsonFetch<DocumentType>(`${BASE}/types/${vars.key}`, { method: 'PATCH', body: vars.values }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
